import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, Observable, switchMap, throwError } from "rxjs";
import { environment } from "../../../../../environments/environment";
import {
  BatchProductResponse,
  BundleItemDto,
  CreateOrEditBranchPrice,
  CreateProductResponse,
  Product,
  ProductDtoV2Response,
  ProductListFilter,
  ProductPriceDto,
  UpdateStockDto,
} from "../../../../models/product-v2";
import { AddSpecifications } from "../../../../models/specifications";
import { buildQueryParamsFromObject } from "../../../../util/utils";
import { FileExportDto } from "../../collection/services/collection.service";

@Injectable({
  providedIn: "root",
})
export class ProductV2Service {
  constructor(private readonly _http: HttpClient) { }

  list(
    storeId: string,
    filter: ProductListFilter
  ): Observable<ProductDtoV2Response> {
  
    const params = buildQueryParamsFromObject({
      ...filter,
      _t: Date.now() // cache buster
    });
  
    return this._http.get<ProductDtoV2Response>(
      `${environment.url}/management/product/v2/store/${storeId}`,
      { params }
    );
  }
  

  get(storeId: string, id: string): Observable<Product> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/${id}`;
    return this._http.get<Product>(url);
  }


  update(product: Partial<Product>, storeId: string, productId: string): Observable<void> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/${productId}`;
    return this._http.put<void>(url, product);
  }
  addSpecifications(specifications: AddSpecifications[], storeId: string, productId: string): Observable<void> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/${productId}/specifications`;
    return this._http.put<void>(url, specifications);
  }


  create(product: Partial<Product>, storeId: string): Observable<CreateProductResponse> {
    const url = `${environment.url}/management/product/v2/store/${storeId}`;
    return this._http.post<CreateProductResponse>(url, product);
  }
  createPack(product: Partial<Product>, storeId: string): Observable<CreateProductResponse> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/bundles`;
    return this._http.post<CreateProductResponse>(url, product);
  }

  addItemsToBundle(storeId: string, productId: string, items: BundleItemDto[]): Observable<void> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/products/${productId}/bundles/items`;
    return this._http.put<void>(url, items);
  }
  deleteItemFromBundle(storeId: string, bundleId: string, productId: string): Observable<void> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/products/${bundleId}/bundles/items/${productId}`;
    return this._http.delete<void>(url);
  }
  updateItemInBundle(
    storeId: string,
    bundleId: string,
    productId: string,
    payload: Partial<BundleItemDto>
  ): Observable<void> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/products/${bundleId}/bundles/items/${productId}`;
    return this._http.put<void>(url, payload);
  }


  upload(storeId: string, file: FormData): Observable<BatchProductResponse> {
    return this._http.post<BatchProductResponse>(
      `${environment.url}/management/product/v2/store/${storeId}/upload`,
      file
    );
  }
  updateFile(storeId: string, file: FormData): Observable<BatchProductResponse> {
    return this._http.put<BatchProductResponse>(
      `${environment.url}/management/product/v2/store/${storeId}/upload`,
      file
    );
  }

  uploadRelated(storeId: string, file: FormData): Observable<BatchProductResponse> {
    return this._http.post<BatchProductResponse>(
      `${environment.url}/management/product/v2/store/${storeId}/upload/related`,
      file
    );
  }

  uploadStock(storeId: string, file: FormData): Observable<BatchProductResponse> {
    return this._http.post<BatchProductResponse>(
      `${environment.url}/management/product/v2/store/${storeId}/upload/stock`,
      file
    );
  }

  addStock(
    storeId: string,
    productId: string,
    body: UpdateStockDto[]
  ): Observable<void> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/stock/${productId}`;
    return this._http.put<void>(url, body);
  }
  addPrices(
    storeId: string,
    productId: string,
    body: CreateOrEditBranchPrice[]
  ): Observable<void> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/prices/${productId}`;
    return this._http.post<void>(url, body);
  }
  deletePrice(
    storeId: string,
    productId: string,
    priceId: string
  ): Observable<void> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/prices/${productId}/${priceId}`;
    return this._http.delete<void>(url);
  }

  disable(storeId: string, productIds: string[]): Observable<void> {
    return this._http.put<void>(
      `${environment.url}/management/product/v2/store/${storeId}/disable-products`,
      productIds
    );
  }

  enable(storeId: string, productIds: string[]): Observable<void> {
    return this._http.put<void>(
      `${environment.url}/management/product/v2/store/${storeId}/enable-products`,
      productIds
    );
  }

  addCrossSellingProducts(
    storeId: string,
    productId: string,
    body: string[]
  ): Observable<void> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/${productId}/crossselling`;
    return this._http.put<void>(url, body);
  }

  deleteCrossSellingProduct(
    storeId: string,
    productId: string,
    relatedProductId: string
  ): Observable<void> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/${productId}/crossselling/${relatedProductId}`;
    return this._http.delete<void>(url);
  }

  addUpSellingProducts(
    storeId: string,
    productId: string,
    body: string[]
  ): Observable<void> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/${productId}/upselling`;
    return this._http.put<void>(url, body);
  }

  deleteUpSellingProduct(
    storeId: string,
    productId: string,
    relatedProductId: string
  ): Observable<void> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/${productId}/upselling/${relatedProductId}`;
    return this._http.delete<void>(url);
  }

  getProductPrices(
    storeId: string,
    productId: string
  ): Observable<ProductPriceDto[]> {
    const url = `${environment.url}/management/product/v2/store/${storeId}/prices/${productId}`;
    return this._http.get<ProductPriceDto[]>(url);
  }
  
  
  exportProductsToExcel(
    storeId: string,
    filters: any,
    to?: string[]
  ): Observable<FileExportDto | { message: string }> {
    const payload = { filters, to };
  
    return this._http
      .post<Blob>(
        `${environment.url}/management/product/v2/store/${storeId}/products/export/excel`,
        payload,
        { observe: 'response', responseType: 'blob' as 'json' }
      )
      .pipe(
        switchMap((response: HttpResponse<Blob>) => {
          const contentType = response.headers.get('content-type') ?? '';
  
          if (contentType.includes('application/json')) {
            return new Observable<{ message: string }>((observer) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result as string);
                  observer.next(json);
                } catch (e) {
                  observer.error(e);
                } finally {
                  observer.complete();
                }
              };
              reader.readAsText(response.body as Blob);
            });
          }
  
          return new Observable<FileExportDto>((observer) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64Data = (reader.result as string).split(',')[1];
              observer.next(FileExportDto.fromJS({
                fileName: 'productos.xlsx',
                fileContent: base64Data
              }));
              observer.complete();
            };
            reader.onerror = (err) => observer.error(err);
            reader.readAsDataURL(response.body as Blob);
          });
        }),
        catchError((err) => throwError(() => err))
      );
  }
  
  
  
  
  exportPricesToExcel(
    storeId: string,
    filters: any,
    to?: string[]
  ): Observable<FileExportDto | { message: string }> {
    const payload = { filters, to };
    return this._http
      .post<Blob>(
        `${environment.url}/management/product/v2/store/${storeId}/prices/export/excel`,
        payload,
        { observe: 'response', responseType: 'blob' as 'json' }
      )
      .pipe(
        switchMap((response: HttpResponse<Blob>) => {
          const contentType = response.headers.get('content-type') ?? '';
  
          if (contentType.includes('application/json')) {
            return new Observable<{ message: string }>((observer) => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result as string);
                  observer.next(json);
                } catch (e) {
                  observer.error(e);
                } finally {
                  observer.complete();
                }
              };
              reader.readAsText(response.body as Blob);
            });
          }
  
          return new Observable<FileExportDto>((observer) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64Data = (reader.result as string).split(',')[1];
              observer.next(FileExportDto.fromJS({
                fileName: 'precios.xlsx',
                fileContent: base64Data
              }));
              observer.complete();
            };
            reader.onerror = (err) => observer.error(err);
            reader.readAsDataURL(response.body as Blob);
          });
        }),
        catchError((err) => throwError(() => err))
      );
  }
  
  
  
  
  
  exportImagesToExcel(storeId: string, filters: any): Observable<FileExportDto> {
    return this._http.post<FileExportDto>(
      `${environment.url}/management/product/v2/store/${storeId}/products/exportImages/excel`,
      filters
    );
  }
  uploadImagesExcel(storeId: string, file: FormData, to?: string): Observable<any> {
    let params = new HttpParams();
    if (to) {
      params = params.append('to', to);
    }
  
    return this._http.post<any>(
      `${environment.url}/management/product/v2/store/${storeId}/upload/images`,
      file,
      { params }
    );
  }
  exportProductSpecificationsToExcel(storeId: string, categoryId: string): Observable<FileExportDto> {
    
  
    return this._http.get<FileExportDto>(
      `${environment.url}/management/product/v2/store/${storeId}/${categoryId}/specifications/export/excel`
    );
  }
  uploadSpecifications(storeId: string, formData: FormData) {
    return this._http.post<any>(
      `${environment.url}/management/product/v2/store/${storeId}/specifications/upload`, 
      formData
    );
  }
  uploadPrices(storeId: string, formData: FormData) {
    return this._http.post<any>(
      `${environment.url}/management/product/v2/store/${storeId}/batch/upload`,
      formData
    );
  }
  uploadPricesPromotional(storeId: string, formData: FormData) {
    return this._http.post<any>(
      `${environment.url}/management/price/store/${storeId}/upload-promotional`,
      formData
    );
  }
}
